// frontend/src/pages/Goals/Goals.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, LinearProgress,
  CircularProgress, Alert, TextField, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tabs, Tab
} from '@mui/material';
import {
  Add, Delete, CheckCircle, Close, Refresh, EmojiEvents,
  TrendingUp, Speed, LocalFireDepartment, MenuBook, Edit
} from '@mui/icons-material';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';
import api from '../../services/api';

const goalTypeConfig = {
  accuracy: { label: 'Accuracy Goal', icon: <TrendingUp />, color: '#2196f3' },
  volume: { label: 'Volume Goal', icon: <Speed />, color: '#ff9800' },
  streak: { label: 'Streak Goal', icon: <LocalFireDepartment />, color: '#f44336' },
  topic_completion: { label: 'Topic Completion', icon: <MenuBook />, color: '#9c27b0' },
  custom: { label: 'Custom Goal', icon: <Edit />, color: '#607d8b' }
};

function CreateGoalDialog({ open, onClose, onCreate, topics, loading }) {
  const [type, setType] = useState('accuracy');
  const [topic, setTopic] = useState('');
  const [targetAccuracy, setTargetAccuracy] = useState(80);
  const [targetCount, setTargetCount] = useState(50);
  const [targetStreak, setTargetStreak] = useState(7);
  const [difficulty, setDifficulty] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);

  const handleCreate = () => {
    const params = {};
    if (type === 'accuracy') { params.topic = topic; params.targetAccuracy = targetAccuracy; }
    else if (type === 'volume') { params.targetCount = targetCount; }
    else if (type === 'streak') { params.targetStreak = targetStreak; }
    else if (type === 'topic_completion') { params.topic = topic; if (difficulty) params.difficulty = difficulty; }
    else if (type === 'custom') { params.description = description; }

    onCreate({ type, parameters: params, targetDate });
  };

  const isValid = () => {
    if (!targetDate) return false;
    if ((type === 'accuracy' || type === 'topic_completion') && !topic) return false;
    if (type === 'custom' && !description) return false;
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set a New Goal</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Goal Type</InputLabel>
          <Select value={type} onChange={e => setType(e.target.value)} label="Goal Type">
            {Object.entries(goalTypeConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(type === 'accuracy' || type === 'topic_completion') && (
          <FormControl fullWidth size="small">
            <InputLabel>Topic</InputLabel>
            <Select value={topic} onChange={e => setTopic(e.target.value)} label="Topic">
              {topics.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
            </Select>
          </FormControl>
        )}

        {type === 'accuracy' && (
          <TextField label="Target Accuracy (%)" type="number" value={targetAccuracy}
            onChange={e => setTargetAccuracy(Math.min(100, Math.max(1, parseInt(e.target.value) || 0)))}
            inputProps={{ min: 1, max: 100 }} size="small" fullWidth
            helperText="What accuracy do you want to reach?" />
        )}

        {type === 'volume' && (
          <TextField label="Questions Per Week" type="number" value={targetCount}
            onChange={e => setTargetCount(Math.max(1, parseInt(e.target.value) || 0))}
            inputProps={{ min: 1 }} size="small" fullWidth
            helperText="How many questions per week?" />
        )}

        {type === 'streak' && (
          <TextField label="Target Streak (days)" type="number" value={targetStreak}
            onChange={e => setTargetStreak(Math.max(1, parseInt(e.target.value) || 0))}
            inputProps={{ min: 1 }} size="small" fullWidth
            helperText="How many consecutive study days?" />
        )}

        {type === 'topic_completion' && (
          <FormControl fullWidth size="small">
            <InputLabel>Difficulty (optional)</InputLabel>
            <Select value={difficulty} onChange={e => setDifficulty(e.target.value)} label="Difficulty (optional)">
              <MenuItem value="">All difficulties</MenuItem>
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </Select>
          </FormControl>
        )}

        {type === 'custom' && (
          <TextField label="Goal Description" value={description}
            onChange={e => setDescription(e.target.value)} multiline rows={2}
            size="small" fullWidth placeholder="e.g. Review notes before each study session" />
        )}

        <TextField label="Target Date" type="date" value={targetDate}
          onChange={e => setTargetDate(e.target.value)} InputLabelProps={{ shrink: true }}
          inputProps={{ min: minDate.toISOString().split('T')[0] }} fullWidth size="small" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !isValid()}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}>
          Set Goal
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GoalCard({ goal, onCancel, onCheckIn, onRefresh }) {
  const config = goalTypeConfig[goal.type] || goalTypeConfig.custom;
  const daysLeft = Math.max(0, Math.ceil((new Date(goal.targetDate) - new Date()) / 86400000));
  const isAchieved = goal.status === 'achieved';
  const isMissed = goal.status === 'missed';

  return (
    <Card sx={{
      mb: 1.5, borderLeft: `4px solid ${isAchieved ? '#4caf50' : isMissed ? '#f44336' : config.color}`,
      opacity: (isMissed || goal.status === 'cancelled') ? 0.6 : 1
    }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
            <Box sx={{ color: config.color }}>{config.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{goal.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {isAchieved ? 'Achieved ' + new Date(goal.achievedAt).toLocaleDateString('en-ZA') :
                  isMissed ? 'Missed — target was ' + new Date(goal.targetDate).toLocaleDateString('en-ZA') :
                  daysLeft + ' days remaining'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isAchieved && <EmojiEvents sx={{ color: '#ffc107' }} />}
            <Chip label={goal.status} size="small"
              color={isAchieved ? 'success' : isMissed ? 'error' : 'default'} variant="outlined" />
          </Box>
        </Box>

        {goal.status === 'active' && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {goal.progress.currentValue} / {goal.progress.targetValue}
                {goal.type === 'accuracy' ? '%' : goal.type === 'streak' ? ' days' : goal.type === 'volume' ? ' questions' : '%'}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{goal.progress.percentComplete}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={goal.progress.percentComplete}
              sx={{ height: 6, borderRadius: 3 }}
              color={goal.progress.percentComplete >= 80 ? 'success' : goal.progress.percentComplete >= 40 ? 'warning' : 'primary'} />
          </Box>
        )}

        {goal.status === 'active' && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            {goal.type === 'custom' && (
              <Button size="small" variant="outlined" startIcon={<CheckCircle />}
                onClick={() => onCheckIn(goal._id)}>Check In</Button>
            )}
            <Button size="small" variant="text" color="inherit" startIcon={<Close />}
              onClick={() => onCancel(goal._id)} sx={{ opacity: 0.6 }}>Cancel</Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function GoalsContent() {
  const [goals, setGoals] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('active');

  const fetchGoals = useCallback(async () => {
    try { setLoading(true); const res = await api.get('/api/goals'); if (res.data.success) setGoals(res.data.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const fetchTopics = useCallback(async () => {
    try { const res = await api.get('/api/study-plan/topics'); if (res.data.success) setTopics(res.data.data.topics); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchGoals(); fetchTopics(); }, [fetchGoals, fetchTopics]);

  const handleCreate = async (data) => {
    try {
      setCreating(true); setError(null);
      const res = await api.post('/api/goals', data);
      if (res.data.success) { setGoals(prev => [res.data.data, ...prev]); setDialogOpen(false); }
    } catch (err) { setError(err.response?.data?.error || 'Failed to create goal'); }
    finally { setCreating(false); }
  };

  const handleCancel = async (id) => {
    try { await api.delete(`/api/goals/${id}`); setGoals(prev => prev.map(g => g._id === id ? { ...g, status: 'cancelled' } : g)); }
    catch (err) { console.error(err); }
  };

  const handleCheckIn = async (id) => {
    try {
      const res = await api.post(`/api/goals/${id}/check-in`);
      if (res.data.success) setGoals(prev => prev.map(g => g._id === id ? { ...res.data.data, title: g.title } : g));
    } catch (err) { console.error(err); }
  };

  const handleRefresh = async () => {
    try { setRefreshing(true); await api.post('/api/goals/refresh'); await fetchGoals(); }
    catch (err) { console.error(err); } finally { setRefreshing(false); }
  };

  const filtered = goals.filter(g => {
    if (tab === 'active') return g.status === 'active';
    if (tab === 'achieved') return g.status === 'achieved';
    if (tab === 'past') return g.status === 'missed' || g.status === 'cancelled';
    return true;
  });

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>My Goals</Typography>
          <Typography variant="body2" color="text.secondary">Set targets and track your progress</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh} disabled={refreshing}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>New Goal</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${goals.filter(g => g.status === 'active').length})`} value="active" />
        <Tab label={`Achieved (${goals.filter(g => g.status === 'achieved').length})`} value="achieved" />
        <Tab label="Past" value="past" />
      </Tabs>

      {filtered.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            {tab === 'active' ? (
              <>
                <EmojiEvents sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">No active goals</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set a goal to stay motivated and track your progress.
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
                  Set Your First Goal
                </Button>
              </>
            ) : tab === 'achieved' ? (
              <>
                <Typography variant="h6" color="text.secondary">No achievements yet</Typography>
                <Typography variant="body2" color="text.secondary">Keep working toward your active goals!</Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No past goals</Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        filtered.map(g => (
          <GoalCard key={g._id} goal={g} onCancel={handleCancel} onCheckIn={handleCheckIn} onRefresh={handleRefresh} />
        ))
      )}

      <CreateGoalDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        onCreate={handleCreate} topics={topics} loading={creating} />
    </Box>
  );
}

export default function GoalsPage() {
  return (<FeatureGate feature="goal_setting" fallback={<UpgradePrompt feature="goal_setting" />}><GoalsContent /></FeatureGate>);
}
